import React, { useEffect } from 'react';
import { Button, Card, Switch, cardSurfaceClassName } from '../../../../shared/ui';
import { sectionHeading, cardTitle, cardDescription, cardPadding, saveRow, saveButton } from '../profileStyles';
import { useNotificationStore } from '../../store/notificationStore';
import { useUserStore } from '../../../auth/store/userStore';
import { showToast } from '../../../../shared/services/toast';
import { useDataNetStore } from '../../../../shared/store/dataNetStore';

const DATANET_KEY = 'datanet';

export function NotificationSection({ onLoadingChange, dataTestId }) {
  const baseTestId = dataTestId || 'profile-notifications-section';
  const {
    notificationSettings,
    isLoading,
    isSaving,
    getNotificationSettings,
    setNotificationCategory,
    setSaving,
  } = useNotificationStore();

  const { profile } = useUserStore();
  const { setUserDataNet } = useDataNetStore();

  const userId = profile?.userId;

  // Every toggle is described by the API: label, sub-label and order all come from
  // master.notification_categories, and the server has already dropped the ones this user is not
  // offered (hidden categories, billing without the role, DataNet without the subscription).
  // Nothing is hardcoded here, so a new category needs no change in this file.
  //
  // An empty list is the normal state, not an error — a category ships hidden and is released
  // server-side.
  const categories = notificationSettings.categories || [];
  const hasDataNet = notificationSettings.hasDataNet === true;

  useEffect(() => {
    getNotificationSettings();
  }, [getNotificationSettings]);

  const handleSaveSettings = async () => {
    const { updateNotificationSettings } = await import('../../services/notificationService');
    try {
      // Two loaders, deliberately: setSaving drives this section's own button (label + disabled,
      // which is what stops a double-click saving twice), while onLoadingChange drives the
      // modal-level overlay and is what makes ProfileModal.handleClose refuse to close mid-save.
      setSaving(true);
      onLoadingChange?.(true, 'Updating notification preferences');

      const result = await updateNotificationSettings(notificationSettings);
      if (!result.success) {
        showToast.error(result.message || 'Failed to save notification settings.');
        return;
      }

      // Sync DataNet value to shared store so Dashboard/TeamPage stay in sync
      if (hasDataNet && userId != null) {
        const dataNet = categories.find((c) => c.key === DATANET_KEY);
        if (dataNet) setUserDataNet(userId, dataNet.isEnabled);
      }

      showToast.success('Notification preferences saved successfully!');
    } catch (err) {
      showToast.error(err.message || 'Failed to update notification settings.');
    } finally {
      setSaving(false);
      onLoadingChange?.(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card className={`shadow-none ${cardSurfaceClassName}`}>
          <div className="p-6">
            <div className="animate-pulse space-y-4">
              <div className="h-4 bg-surface-disabled rounded w-1/4"></div>
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="flex justify-between items-center">
                    <div className="space-y-2">
                      <div className="h-3 bg-surface-disabled rounded w-32"></div>
                      <div className="h-3 bg-surface-disabled rounded w-48"></div>
                    </div>
                    <div className="h-6 bg-surface-disabled rounded w-12"></div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid={baseTestId}>
      <h3 className={sectionHeading} data-testid={`${baseTestId}-header`}>Preferences</h3>

      {categories.map((category) => (
        <Card className={`shadow-none ${cardSurfaceClassName}`} key={category.key} data-testid={`${baseTestId}-card-${category.key}`}>
          <div className={`${cardPadding} flex items-center justify-between`}>
            <div className="flex-1">
              <h4 className={cardTitle} data-testid={`${baseTestId}-label-${category.key}`}>{category.label}</h4>
              {category.description && (
                <p className={cardDescription} data-testid={`${baseTestId}-desc-${category.key}`}>{category.description}</p>
              )}
            </div>
            <div className="ml-4">
              <Switch
                checked={category.isEnabled}
                onCheckedChange={(value) => setNotificationCategory(category.key, value)}
                data-testid={`${baseTestId}-toggle-${category.key}`}
              />
            </div>
          </div>
        </Card>
      ))}

      <div className={saveRow}>
        <Button
          type="button"
          onClick={handleSaveSettings}
          disabled={isSaving}
          className={saveButton}
          data-testid={`${baseTestId}-save-button`}
        >
          {isSaving ? 'Saving...' : 'Save Preferences'}
        </Button>
      </div>
    </div>
  );
}
